import { useRef } from 'react';

import { ExpandMore } from '@mui/icons-material';
import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useLanguage } from '../../hooks';
import { AssetSummary, AssetTypeWithAssets } from '../../types/asset';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { getAssetTypeIconColors, getAssetTypeIconComponent } from './utils/assetTypeIcons';

const customStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  assetTypeRow: {
    border: '1px solid #DDE0E5',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  assetTypeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#F8F9FA',
    },
  },
  assetTypeHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    width: '24px',
    height: '24px',
    flexShrink: 0,
  },
  assetTypeName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#3B3C40',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '170px',
  },
  tagsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  countChip: {
    height: '22px',
    fontSize: '12px',
    borderRadius: '4px',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
  assetsCountChip: {
    backgroundColor: '#D8EFFE',
    border: '1px solid #8BC6FD',
    color: (theme) => theme.palette.primary.main,
  },
  selectedCountChip: {
    backgroundColor: '#F0F5FF',
    border: '1px solid #ADC6FF',
    color: '#1D39C4',
  },
  chevron: {
    transition: 'transform 0.2s',
    color: '#6E7178',
    fontSize: '20px',
  },
  chevronExpanded: {
    transform: 'rotate(180deg)',
  },
  typeHeaderCompact: {
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    padding: '0 12px',
    boxSizing: 'border-box',
  },
  typeHeaderLeftCompact: {
    flex: 1,
    minWidth: 0,
    gap: '8px',
  },
  assetTypeNameCompact: {
    flex: 1,
    minWidth: 0,
    maxWidth: 'none',
  },
  selectorCheckbox: {
    '&.MuiCheckbox-root': {
      padding: 0,
      margin: 0,
      marginLeft: 0,
      width: '16px',
      height: '16px',
      minWidth: '16px',
      minHeight: '16px',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '16px',
      width: '16px',
      height: '16px',
    },
  },
  assetList: {
    borderTop: '1px solid #DDE0E5',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  assetListViewport: {
    minHeight: 0,
  },
  virtualListInner: {
    width: '100%',
    position: 'relative',
  },
  virtualListRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
  },
  assetTypeIcon: {
    fontSize: '16px',
  },
  assetItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 16px 10px 48px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F0F5FF',
    },
  },
  assetName: {
    fontSize: '14px',
    color: '#46484D',
    margin: 0,
    lineHeight: 1.25,
  },
  flatAssetList: {
    borderTop: '1px solid #DDE0E5',
  },
} satisfies Record<string, SxProps<Theme>>;

const ASSET_ITEM_HEIGHT = 44;
const MAX_VISIBLE_ITEMS = 10;

interface AssetListProps {
  assetTypeId: number;
  assets: AssetSummary[];
  isAssetSelected: (assetTypeId: number, assetId: number) => boolean;
  toggleAsset: (assetTypeId: number, assetId: number, allAssets: AssetSummary[]) => void;
}

function VirtualizedAssetList({
  assetTypeId,
  assets,
  isAssetSelected,
  toggleAsset,
}: AssetListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: assets.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ASSET_ITEM_HEIGHT,
    overscan: 5,
  });

  const maxViewportHeight = MAX_VISIBLE_ITEMS * ASSET_ITEM_HEIGHT;
  const virtualTotalSize = rowVirtualizer.getTotalSize();
  const listHeight =
    assets.length === 0 ? 0 : Math.min(maxViewportHeight, virtualTotalSize);

  return (
    <Box
      ref={scrollRef}
      sx={[customStyles.assetList, customStyles.assetListViewport]}
      style={{ height: listHeight }}
    >
      <Box
        sx={customStyles.virtualListInner}
        style={{ height: rowVirtualizer.getTotalSize() }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const asset = assets[virtualItem.index];
          return (
            <Box
              key={String(virtualItem.key)}
              data-index={virtualItem.index}
              ref={rowVirtualizer.measureElement}
              sx={[customStyles.assetItem, customStyles.virtualListRow]}
              style={{ transform: `translateY(${virtualItem.start}px)` }}
              onClick={() => toggleAsset(assetTypeId, asset.id, assets)}
            >
              <Checkbox
                checked={isAssetSelected(assetTypeId, asset.id)}
                sx={customStyles.selectorCheckbox}
              />
              <Typography sx={customStyles.assetName}>
                {asset.name} | {asset.code}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function FlatAssetList({
  assetTypeId,
  assets,
  isAssetSelected,
  toggleAsset,
}: AssetListProps) {
  return (
    <Box sx={customStyles.flatAssetList}>
      {assets.map((asset) => (
        <Box
          key={asset.id}
          sx={customStyles.assetItem}
          onClick={() => toggleAsset(assetTypeId, asset.id, assets)}
        >
          <Checkbox
            checked={isAssetSelected(assetTypeId, asset.id)}
            sx={customStyles.selectorCheckbox}
          />
          <Typography sx={customStyles.assetName}>
            {asset.name} | {asset.code}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

interface AssetTypesMultipleSelectorProps {
  assetTypes: AssetTypeWithAssets[];
  expandedAssetTypeIds: number[];
  toggleAssetType: (assetTypeId: number) => void;
  toggleAssetTypeExpansion: (assetTypeId: number) => void;
  toggleAsset: (assetTypeId: number, assetId: number, allAssets: AssetSummary[]) => void;
  isAssetTypeSelected: (assetTypeId: number) => boolean;
  isAssetSelected: (assetTypeId: number, assetId: number) => boolean;
  getSelectedAssetsCount: (assetTypeId: number, totalAssets: number) => number;
  /** Compact type row height 40px (e.g. findings filter). */
  compactTypeHeader?: boolean;
  /** Renders a flat list instead of a virtualized one, delegating scroll to the parent container. */
  disableInternalScroll?: boolean;
}

export function AssetTypesMultipleSelector({
  assetTypes,
  expandedAssetTypeIds,
  toggleAssetType,
  toggleAssetTypeExpansion,
  toggleAsset,
  isAssetTypeSelected,
  isAssetSelected,
  getSelectedAssetsCount,
  compactTypeHeader = false,
  disableInternalScroll = false,
}: AssetTypesMultipleSelectorProps) {
  const lang = useLanguage();

  const handleSelectAssetType = (event: React.MouseEvent<HTMLDivElement>, assetTypeId: number) => {
    event.stopPropagation();
    toggleAssetType(assetTypeId);
  };

  return (
    <Box sx={customStyles.container}>
      {assetTypes.map((assetType) => {
        const isExpanded = expandedAssetTypeIds.includes(assetType.id);
        const isSelected = isAssetTypeSelected(assetType.id);
        const selectedCount = getSelectedAssetsCount(assetType.id, assetType.assets_count);
        const IconComponent = getAssetTypeIconComponent(assetType.icon ?? undefined);
        const { fill, stroke } = getAssetTypeIconColors(assetType.icon_color ?? undefined);

        return (
          <Box
            key={assetType.id}
            sx={customStyles.assetTypeRow}
          >
            <Box
              sx={
                compactTypeHeader
                  ? { ...customStyles.assetTypeHeader, ...customStyles.typeHeaderCompact }
                  : customStyles.assetTypeHeader
              }
              onClick={() => toggleAssetTypeExpansion(assetType.id)}
            >
              <Box
                sx={
                  compactTypeHeader
                    ? { ...customStyles.assetTypeHeaderLeft, ...customStyles.typeHeaderLeftCompact }
                    : customStyles.assetTypeHeaderLeft
                }
              >
                <Box onClick={(event) => handleSelectAssetType(event, assetType.id)}>
                  <Checkbox
                    checked={isSelected}
                    sx={customStyles.selectorCheckbox}
                  />
                </Box>
                <Box
                  sx={{
                    ...customStyles.iconWrapper,
                    backgroundColor: fill,
                  }}
                >
                  <IconComponent
                    sx={customStyles.assetTypeIcon}
                    stroke={stroke}
                  />
                </Box>
                <Typography
                  sx={
                    compactTypeHeader
                      ? { ...customStyles.assetTypeName, ...customStyles.assetTypeNameCompact }
                      : customStyles.assetTypeName
                  }
                >
                  {assetType.name}
                </Typography>
                <Box sx={customStyles.tagsRow}>
                  <Chip
                    label={`${assetType.assets_count} ${lang.mainMenu.assets}`}
                    size='small'
                    sx={[customStyles.countChip, customStyles.assetsCountChip]}
                  />
                  {selectedCount > 0 && (
                    <Chip
                      label={lang.assets.selectedCount.replace('{count}', String(selectedCount))}
                      size='small'
                      sx={[customStyles.countChip, customStyles.selectedCountChip]}
                    />
                  )}
                </Box>
              </Box>
              <ExpandMore
                sx={[customStyles.chevron, ...(isExpanded ? [customStyles.chevronExpanded] : [])]}
              />
            </Box>

            {isExpanded && (
              disableInternalScroll ? (
                <FlatAssetList
                  assetTypeId={assetType.id}
                  assets={assetType.assets}
                  isAssetSelected={isAssetSelected}
                  toggleAsset={toggleAsset}
                />
              ) : (
                <VirtualizedAssetList
                  assetTypeId={assetType.id}
                  assets={assetType.assets}
                  isAssetSelected={isAssetSelected}
                  toggleAsset={toggleAsset}
                />
              )
            )}
          </Box>
        );
      })}
    </Box>
  );
}
